import { useEffect, useState } from "react";
import { Timer } from "lucide-react";
import { useI18n } from "@/lib/i18n";

function diffParts(target) {
  const now = Date.now();
  const t = new Date(target).getTime();
  const ms = Math.max(0, t - now);
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { ms, days, hours, minutes, seconds, expired: ms <= 0 };
}

function Cell({ value, label }) {
  return (
    <div className="flex flex-col items-center min-w-[36px]">
      <div className="font-mono font-black text-lg text-amber-100 leading-none tabular-nums" dir="ltr">
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[9px] uppercase tracking-widest text-amber-300/70 mt-0.5">{label}</div>
    </div>
  );
}

/**
 * Countdown ticker for the launch promo. Ticks every second.
 * Shows days:hours:minutes:seconds until `expiresAt` (ISO string).
 */
export default function PromoCountdown({ expiresAt, className = "" }) {
  const { t, dir } = useI18n();
  const [parts, setParts] = useState(() => diffParts(expiresAt));

  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setParts(diffParts(expiresAt)), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt || parts.expired) return null;

  return (
    <div
      dir={dir}
      data-testid="promo-countdown"
      className={"inline-flex items-center gap-2 rounded-lg bg-black/50 border border-amber-500/40 px-3 py-1.5 " + className}
    >
      <Timer className="w-3.5 h-3.5 text-amber-300 shrink-0" />
      <div className="flex items-center gap-2" dir="ltr">
        {parts.days > 0 && (
          <>
            <Cell value={parts.days} label={t("cd_d") || "d"} />
            <span className="text-amber-500/60 font-bold">:</span>
          </>
        )}
        <Cell value={parts.hours} label={t("cd_h") || "h"} />
        <span className="text-amber-500/60 font-bold">:</span>
        <Cell value={parts.minutes} label={t("cd_m") || "m"} />
        <span className="text-amber-500/60 font-bold">:</span>
        <Cell value={parts.seconds} label={t("cd_s") || "s"} />
      </div>
    </div>
  );
}
